/**
 * ValidationIcon Component
 *
 * Displays warning or info icons for field validation.
 * Shows tooltips with validation messages and recommendations.
 */

import { Tooltip, Box, SxProps } from '@mui/material';
import { Warning as WarningIcon, Info as InfoIcon } from '@mui/icons-material';

interface ValidationIconProps {
  validations: { severity: 'warning' | 'info'; message: string; recommendation?: string }[];
  sx?: SxProps;
}

export function ValidationIcon({ validations, sx }: ValidationIconProps) {
  if (validations.length === 0) return null;

  const warnings = validations.filter(v => v.severity === 'warning');
  const infos = validations.filter(v => v.severity === 'info');

  // Show warning if any warnings, otherwise show info
  const primaryValidation = warnings.length > 0 ? warnings[0] : infos[0];
  const count = validations.length;

  const icon = primaryValidation.severity === 'warning' ? <WarningIcon /> : <InfoIcon />;

  return (
    <Tooltip
      title={
        <div>
          {validations.map((v, i) => (
            <div key={i}>
              <strong>{v.message}</strong>
              {v.recommendation && (
                <div style={{ marginTop: 4, fontSize: '0.9em' }}>
                  💡 {v.recommendation}
                </div>
              )}
            </div>
          ))}
        </div>
      }
      arrow
      placement="top"
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: primaryValidation.severity === 'warning' ? 'warning.main' : 'info.main',
          marginLeft: 1,
          ...sx
        }}
      >
        {icon}
        {count > 1 && (
          <span style={{ marginLeft: 2, fontSize: '0.8em', fontWeight: 'bold' }}>
            {count}
          </span>
        )}
      </Box>
    </Tooltip>
  );
}
